// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import sendActivationEmail from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SendActivationEmail";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    // XNG-Changes
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
});

const mapDispatchToProps = {
    setUserFields,
    sendActivationEmail,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface UserSetupStatusStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    activationStatus?: CheckboxPresentationProps;
    lastSignInLabel?: TypographyPresentationProps;
    lastSignInText?: TypographyPresentationProps;
    accountStatusLabel?: TypographyPresentationProps;
    accountStatusText?: TypographyPresentationProps;
    sendActivationEmailButton?: ButtonPresentationProps;
    // XNG-Changes
    lastSignInTextField?: TextFieldPresentationProps;
}

export const userSetupStatusStyles: UserSetupStatusStyles = {
    title: { variant: "h5" },
    wrapper: {
        css: css`
            padding-bottom: 15px;
            font-weight: 400;
        `,
    },
    lastSignInLabel: {
        css: css`
            margin-bottom: 5px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    accountStatusLabel: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    accountStatusText: {
        css: css`
            display: block;
            margin-bottom: 10px;
        `,
    },
    sendActivationEmailButton: {
        variant: "secondary",
    },
};

const styles = userSetupStatusStyles;

const UserSetupStatus = ({
    editingUser,
    toaster,
    setUserFields,
    sendActivationEmail,
    // XNG-Changes
    isMobileScreen,
}: Props) => {
    const [activationEmailSent, setActivationEmailSent] = React.useState(false);
    if (!editingUser) {
        return null;
    }

    const activationStatusChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setUserFields({ isApproved: value });
    };

    const sendActivationEmailClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        sendActivationEmail({
            userName: editingUser.userName,
            onSuccess: () => {
                setActivationEmailSent(true);
                toaster.addToast({ body: translate("Activation email sent."), messageType: "success" });
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupStatus_statusLabel">
                {translate("Status")}
            </Typography>

            <StyledWrapper {...styles.wrapper}>
                {/* XNG-changes */}
                {/* <Typography
                    as="p"
                    {...styles.lastSignInLabel}
                    id="lastSignIn"
                    data-test-selector="userSetupStatus_lastSignIn"
                >
                    {translate("Last Sign In")}
                </Typography> */}
                {/* {editingUser.lastLoginOn && (
                    <Typography
                        as="p"
                        {...styles.lastSignInText}
                        aria-labelledby="lastSignIn"
                        data-test-selector="userSetupStatus_lastSignInText"
                    >
                        <LocalizedDateTime
                            dateTime={editingUser.lastLoginOn}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                )} */}
                <TextField
                    value={editingUser.lastLoginOn?.toDateString() || ""}
                    label={translate("Last Sign In")}
                    {...styles.lastSignInTextField}
                    aria-labelledby="lastSignIn"
                    type="lastSignIn"
                    disabled
                    labelPosition={isMobileScreen ? "top" : "left"}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <Checkbox
                    {...styles.activationStatus}
                    checked={!!editingUser.isApproved}
                    onChange={activationStatusChangeHandler}
                    data-test-selector="userSetupStatus_activationStatus"
                    labelPosition="left"
                >
                    {translate("Active")}
                </Checkbox>
            </StyledWrapper>
            {editingUser.activationStatus !== "Activated" && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h3" {...styles.accountStatusLabel} id="accountStatus">
                        {translate("Account Status")}
                    </Typography>
                    <Typography {...styles.accountStatusText} aria-labelledby="accountStatus">
                        {!activationEmailSent &&
                            editingUser.activationStatus === "EmailNotSent" &&
                            siteMessage("User_Admin_EmailNotSent")}
                        {(activationEmailSent || editingUser.activationStatus === "EmailSent") &&
                            siteMessage("User_Admin_EmailSent")}
                    </Typography>
                    <Button
                        {...styles.sendActivationEmailButton}
                        onClick={sendActivationEmailClickHandler}
                        data-test-selector="userSetupStatus_sendActivationEmail"
                    >
                        {translate(
                            activationEmailSent || editingUser.activationStatus === "EmailSent"
                                ? "Resend Activation Email"
                                : "Send Activation Email",
                        )}
                    </Button>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(UserSetupStatus)),
    definition: {
        group: "User Setup",
        displayName: "Status",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
